import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Plus, Search, Users, ArrowRight, Mail, Check, X, Crown } from "lucide-react";
import InvoiceBar from "../components/InvoiceBar";

export default function TeamSetup() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [mode, setMode] = useState(null); // create | join
  const [teamName, setTeamName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [invites, setInvites] = useState([]);
  const [searching, setSearching] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const loadMy = async () => {
    try {
      const { data } = await api.get("/team/my");
      if (data.team) { navigate("/team/dashboard", { replace: true }); return; }
      if (data.pending) setPending(data.pending);
      if (data.pending_invites) setInvites(data.pending_invites);
    } catch {} finally { setInitLoading(false); }
  };

  useEffect(() => {
    // Redirect if already in a team
    loadMy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const acceptInvite = async (token) => {
    setLoading(true);
    try {
      await api.post(`/invite/${token}/accept`);
      toast.success("You're in! Redirecting to your team...");
      await refresh();
      navigate("/team/dashboard");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  const declineInvite = async (token) => {
    if (!window.confirm("Decline this invitation? This can't be undone.")) return;
    setLoading(true);
    try {
      await api.post(`/invite/${token}/decline`);
      toast.success("Invitation declined");
      await loadMy();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  const createTeam = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return toast.error("Enter a team name");
    setLoading(true);
    try {
      await api.post("/team/create", { name: teamName.trim() });
      toast.success("Team created!");
      track("team_created", { funnel_step: 4, action: "create" });
      await refresh();
      navigate("/team/dashboard");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  const searchTeams = async (e) => {
    e?.preventDefault();
    if (!query.trim() || query.trim().length < 2) return toast.error("Type at least 2 characters");
    setSearching(true);
    try {
      const { data } = await api.get("/team/search", { params: { q: query.trim() } });
      setResults(data.teams);
      if (!data.teams.length) toast.info("No teams found");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setSearching(false); }
  };

  const requestJoin = async (team_id) => {
    setLoading(true);
    try {
      await api.post("/team/join-request", { team_id });
      toast.success("Request sent — waiting for leader approval");
      track("team_join_request", { funnel_step: 4, action: "join_request" });
      setResults([]);
      // refresh pending
      const { data } = await api.get("/team/my");
      setPending(data.pending || []);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="App" data-testid="team-setup-page">
      <Navbar />
      {initLoading ? (
        <div className="min-h-[70vh] flex items-center justify-center" data-testid="team-setup-loading">
          <div className="text-center">
            <div className="spinner mx-auto" />
            <div className="label-eyebrow mt-4 text-gray-500">Loading your team</div>
          </div>
        </div>
      ) : (
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        <div className="label-eyebrow mb-3">Welcome, {user?.name}</div>
        <h1 className="font-display text-4xl sm:text-5xl text-white">Set up your <span className="text-brand">team</span></h1>
        <p className="text-gray-400 mt-3 max-w-2xl">You can either create a new team and invite members, or search for an existing team and request to join.</p>

        <InvoiceBar user={user} />

        {invites.length > 0 && (
          <div className="mt-6 card-surface border-l-2 border-brand" data-testid="team-invites-section">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-brand" strokeWidth={2} />
              <div className="label-eyebrow">You have {invites.length === 1 ? "an invitation" : `${invites.length} invitations`} — accept to join instantly</div>
            </div>
            <div className="mt-4 space-y-3">
              {invites.map((inv) => (
                <div key={inv.token} className="flex flex-wrap items-center justify-between gap-3 p-4 border border-white/10 hover:border-brand/40 bg-white/[0.02] transition-colors" data-testid={`invite-${inv.token}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center">
                      <Crown className="w-4 h-4 text-brand" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="font-display text-base text-white">{inv.team_name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Invited by <span className="text-gray-300">{inv.leader_name || "Team Leader"}</span> · sent to <span className="font-mono text-gray-400">{inv.invited_email}</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => acceptInvite(inv.token)}
                      disabled={loading}
                      className="btn-primary flex items-center gap-2 !py-2 !px-4 text-sm"
                      data-testid={`invite-accept-${inv.token}`}
                    >
                      <Check className="w-4 h-4" /> Accept
                    </button>
                    <button
                      onClick={() => declineInvite(inv.token)}
                      disabled={loading}
                      className="btn-secondary flex items-center gap-2 !py-2 !px-4 text-sm"
                      data-testid={`invite-decline-${inv.token}`}
                    >
                      <X className="w-4 h-4" /> Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-4">Don&apos;t see an invite? Ask your team leader to re-send it to the email above.</div>
          </div>
        )}

        {pending.length > 0 && (
          <div className="mt-6 card-surface" data-testid="pending-section">
            <div className="label-eyebrow mb-3">Your Pending Requests</div>
            <div className="space-y-2">
              {pending.map((p) => (
                <div key={p.team_id} className="flex items-center justify-between p-3 border border-white/5">
                  <div>
                    <div className="font-display text-white">{p.team_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{p.team_id}</div>
                  </div>
                  <span className={`badge badge-${p.status}`}>{p.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {/* Create Team */}
          <div className={`card-surface card-hover cursor-pointer ${mode === "create" ? "border-brand/60" : ""}`}
               onClick={() => setMode("create")} data-testid="create-team-card">
            <Plus className="w-8 h-8 text-brand" strokeWidth={1.5} />
            <div className="font-display text-2xl text-white mt-4">Create a Team</div>
            <p className="text-gray-400 mt-2 text-sm">Start fresh. Become the team leader and invite up to 5 teammates.</p>
            {mode === "create" && (
              <form onSubmit={createTeam} className="mt-5 space-y-3 fade-up">
                <input type="text" value={teamName} onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Choose a unique team name" className="input-field"
                  maxLength={60} data-testid="team-name-input" />
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="create-team-submit">
                  {loading ? <span className="spinner" /> : null} Create Team <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>

          {/* Join Team */}
          <div className={`card-surface card-hover cursor-pointer ${mode === "join" ? "border-brand/60" : ""}`}
               onClick={() => setMode("join")} data-testid="join-team-card">
            <Search className="w-8 h-8 text-brand" strokeWidth={1.5} />
            <div className="font-display text-2xl text-white mt-4">Join a Team</div>
            <p className="text-gray-400 mt-2 text-sm">Search by team name or Team ID. Send a join request to the leader.</p>
            {mode === "join" && (
              <div className="mt-5 space-y-3 fade-up">
                <form onSubmit={searchTeams} className="flex gap-2">
                  <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Team name or Team ID" className="input-field" data-testid="team-search-input" />
                  <button type="submit" disabled={searching} className="btn-primary !px-4" data-testid="team-search-btn">
                    {searching ? <span className="spinner" /> : <Search className="w-4 h-4" />}
                  </button>
                </form>
                {results.length > 0 && (
                  <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                    {results.map((t) => (
                      <div key={t.id} className="border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`team-result-${t.id}`}>
                        <div>
                          <div className="font-display text-white">{t.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Lead: {t.leader_name} · <Users className="w-3 h-3 inline" /> {t.member_count}</div>
                          <span className={`badge badge-${t.status} mt-1`}>{t.status}</span>
                        </div>
                        <button onClick={() => requestJoin(t.id)} disabled={loading} className="btn-secondary !py-2 !px-3 text-xs" data-testid={`join-btn-${t.id}`}>Request Join</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      <Footer />
    </div>
  );
}
